import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Divider,
  Chip,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LoginIcon from "@mui/icons-material/Login";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import { useTheme } from '@mui/material/styles';

const StudentProfile = () => {
    const theme = useTheme();
  const token = sessionStorage.getItem("student_token");
  const [studentDetails, setStudentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchStudentDetails();
  }, [navigate, token]);

  const fetchStudentDetails = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.get(
        `${API_BASE_URL}/student/login-details/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setStudentDetails(response.data);
    } catch (err) {
      console.error("Error fetching student details:", err);
      setError("Failed to load student details.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        <Typography
          variant="h6"
          mt={2}
          sx={{ color: "#6b7280", fontWeight: "600" }}
        >
          Loading your profile...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!studentDetails) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No student details found.
        </Typography>
      </Box>
    );
  }

  const StatCard = ({ title, value, icon, gradient, textColor }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: "12px",
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.palette.mode === 'light'
        ? "0 2px 8px rgba(0, 0, 0, 0.04)"
        : "0 2px 8px rgba(0, 0, 0, 0.2)",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.palette.mode === 'light'
          ? "0 8px 24px rgba(102, 126, 234, 0.15)"
          : "0 8px 24px rgba(130, 142, 248, 0.2)",
      },
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            textTransform: "uppercase",
            fontSize: "0.75rem",
            letterSpacing: "0.5px",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            background: gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: theme.palette.mode === 'light' ? textColor : theme.palette.primary.main,
        }}
      >
        {value}
      </Typography>
    </CardContent>
  </Card>
);


  return (
   <Box
  sx={{
    p: { xs: 2, sm: 3, md: 4 },
    maxWidth: 1400,
    mx: "auto",
    minHeight: "100vh",
    background: theme.palette.background.default,
  }}
>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
       <Typography
  variant="h3"
  gutterBottom
  sx={{
    fontWeight: "800",
    background: theme.palette.mode === 'light'
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  }}
>
  Student Profile
</Typography>
<Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
  Manage your account information and monitor login activity
</Typography>

      </Box>

      {/* Profile Overview Section */}
     <Paper
  elevation={0}
  sx={{
    p: 4,
    mb: 3,
    borderRadius: "16px",
    bgcolor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  }}
>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Avatar Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: 200,
            }}
          >
            <Box sx={{ position: "relative", mb: 2 }}>
             <Avatar
  sx={{
    width: 120,
    height: 120,
    background: theme.palette.mode === 'light'
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
    fontSize: "3rem",
    fontWeight: 600,
    boxShadow: theme.palette.mode === 'light'
      ? "0 8px 24px rgba(102, 126, 234, 0.3)"
      : "0 8px 24px rgba(130, 142, 248, 0.3)",
  }}
>
  {studentDetails.student_name.charAt(0).toUpperCase()}
</Avatar>
<Chip
  label="Student"
  size="small"
  sx={{
    position: "absolute",
    bottom: 0,
    right: -10,
    background: theme.palette.mode === 'light'
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "linear-gradient(135deg, #818cf8 0%, #9333ea 100%)",
    color: "white",
    fontWeight: 600,
    fontSize: "0.7rem",
  }}
/>

            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color:  theme.palette.text.primary, textAlign: "center" }}
            >
              {studentDetails.student_name}
            </Typography>
           <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
  {studentDetails.email}
</Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {/* <Chip
                icon={<PersonIcon sx={{ fontSize: "0.9rem" }} />}
                label={`ID: ${studentDetails.student_id}`}
                size="small"
                sx={{ bgcolor: "#f3f4f6", color: "#4b5563", fontWeight: 500 }}
              /> */}
             <Chip
  icon={<SchoolIcon sx={{ fontSize: "0.9rem" }} />}
  label={studentDetails.client_code}
  size="small"
  sx={{ 
    bgcolor: theme.palette.mode === 'light' ? "#f3f4f6" : "rgba(255, 255, 255, 0.1)", 
    color: theme.palette.text.primary, 
    fontWeight: 500 
  }}
/>
            </Box>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" } }}
          />
          {/* Stats Grid */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{ color: "#1f2937", fontWeight: 700, mb: 3 }}
            >
              Account Statistics
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              <StatCard
                title="Total Logins"
                value={studentDetails.total_logins}
                icon={<LoginIcon />}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                textColor="#667eea"
              />
              <StatCard
                title="Total Duration"
                value={`${studentDetails.total_duration_minutes.toFixed(2)} mins`}
                icon={<AccessTimeIcon />}
                gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                textColor="#f5576c"
              />
              <StatCard
                title="Last Login"
                value={
                  studentDetails.last_login
                    ? new Date(studentDetails.last_login).toLocaleString()
                    : "N/A"
                }
                icon={<AccessTimeIcon />}
                gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                textColor="#4facfe"
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Sessions Table */}

      {/* Sessions Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid rgba(102, 126, 234, 0.1)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: "1px solid #e5e7eb" }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 700 }}>
            
            Login Sessions History
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
            Detailed view of all login activities
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
         <TableHead>
  <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? "#f9fafb" : "rgba(30, 41, 59, 0.5)" }}>
    <TableCell
      sx={{
        fontWeight: 600,
        color: theme.palette.text.primary,
        fontSize: "0.875rem",
        py: 2,
      }}
    >
      Login Time
    </TableCell>
    <TableCell
      sx={{
        fontWeight: 600,
        color: theme.palette.text.primary,
        fontSize: "0.875rem",
        py: 2,
      }}
    >
      Logout Time
    </TableCell>
    <TableCell
      sx={{
        fontWeight: 600,
        color: theme.palette.text.primary,
        fontSize: "0.875rem",
        py: 2,
      }}
    >
      Duration
    </TableCell>
    <TableCell
      sx={{
        fontWeight: 600,
        color: theme.palette.text.primary,
        fontSize: "0.875rem",
        py: 2,
      }}
    >
      Status
    </TableCell>
  </TableRow>
</TableHead>


            {/* Table Body with Sorting */}
            <TableBody>
              {[...studentDetails.sessions]
                .sort((a, b) => {
                  // Active session first
                  const aActive = !a.logout_time;
                  const bActive = !b.logout_time;

                  if (aActive && !bActive) return -1;
                  if (!aActive && bActive) return 1;

                  // Then sort by latest login_time (descending)
                  return new Date(b.login_time) - new Date(a.login_time);
                })
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((session, index) => (
              <TableRow
  key={index}
  sx={{
    "&:hover": {
      bgcolor: theme.palette.mode === 'light' ? "#f9fafb" : "rgba(30, 41, 59, 0.3)",
    },
    borderBottom: `1px solid ${theme.palette.divider}`,
  }}
>
  <TableCell sx={{ py: 2.5, color: theme.palette.text.primary }}>
    {new Date(session.login_time).toLocaleString()}
  </TableCell>
  <TableCell sx={{ py: 2.5, color: theme.palette.text.primary }}>
    {session.logout_time ? new Date(session.logout_time).toLocaleString() : "—"}
  </TableCell>
  <TableCell sx={{ py: 2.5 }}>
    <Chip
      label={`${session.duration_minutes.toFixed(2)} mins`}
      size="small"
      sx={{
        bgcolor: theme.palette.mode === 'light' ? "#ede9fe" : "rgba(147, 51, 234, 0.2)",
        color: theme.palette.mode === 'light' ? "#7c3aed" : "#c4b5fd",
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    />
  </TableCell>
  <TableCell sx={{ py: 2.5 }}>
    <Chip
      label={session.logout_time ? "Completed" : "Active"}
      size="small"
      sx={{
        bgcolor: session.logout_time
          ? theme.palette.mode === 'light' ? "#d1fae5" : "rgba(6, 95, 70, 0.2)"
          : theme.palette.mode === 'light' ? "#dbeafe" : "rgba(30, 64, 175, 0.2)",
        color: session.logout_time
          ? theme.palette.mode === 'light' ? "#065f46" : "#10b981"
          : theme.palette.mode === 'light' ? "#1e40af" : "#60a5fa",
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    />
  </TableCell>
</TableRow>

                ))}
            </TableBody>
          </Table>

          {/* Pagination */}
        <TablePagination
  rowsPerPageOptions={[5, 10, 25]}
  component="div"
  count={studentDetails.sessions.length}
  rowsPerPage={rowsPerPage}
  page={page}
  onPageChange={handleChangePage}
  onRowsPerPageChange={handleChangeRowsPerPage}
  sx={{
    borderTop: `1px solid ${theme.palette.divider}`,
    ".MuiTablePagination-select": {
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  }}
/>

        </TableContainer>
      </Paper>
    </Box>
  );
};

export default StudentProfile;